import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { FaSearch, FaMoneyBillWave, FaFileInvoice } from 'react-icons/fa';
import { getPropertyBills, getPropertyRentStatus } from '../../../api/services/billingService';
import { getAdminProperties } from '../../../api/services/adminService';
import { SectionHeader, Badge, EmptyState, LoadingSkeleton, Alert } from './AdminUI';
import './Payments.css';

const formatCurrency = (value) => {
  const n = Number(value || 0);
  return `KES ${n.toLocaleString()}`;
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

const billStatusBadge = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PAID') return <Badge status="success">Paid</Badge>;
  if (s === 'PENDING' || s === 'DUE') return <Badge status="warning">Due</Badge>;
  if (s === 'OVERDUE') return <Badge status="danger">Overdue</Badge>;
  if (s === 'PARTIAL') return <Badge status="info">Partial</Badge>;
  return <Badge status="neutral">{status || '—'}</Badge>;
};

const Payments = () => {
  const [searchInput, setSearchInput] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [bills, setBills] = useState(null);
  const [rentStatus, setRentStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const suggestionsRef = useRef(null);

  // Load bills and rent status for a property
  const loadPropertyData = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    setBills(null);
    setRentStatus(null);
    try {
      const now = new Date();
      const [billsRes, rentRes] = await Promise.allSettled([
        getPropertyBills(id, { page: 0, size: 50 }),
        getPropertyRentStatus(id, { year: now.getFullYear(), month: now.getMonth() + 1 }),
      ]);
      if (billsRes.status === 'fulfilled') {
        const bd = billsRes.value?.data || billsRes.value;
        setBills(bd?.content || bd || []);
      }
      if (rentRes.status === 'fulfilled') {
        setRentStatus(rentRes.value?.data || rentRes.value);
      }
      if (billsRes.status === 'rejected' && rentRes.status === 'rejected') {
        throw billsRes.reason;
      }
    } catch (err) {
      setError(err?.message || 'Failed to load billing data for this property');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch suggestions with debouncing
  useEffect(() => {
    let active = true;
    const query = searchInput.trim();

    const fetchProps = async () => {
      try {
        setSuggestionsLoading(true);
        const res = await getAdminProperties({ search: query || undefined, size: 10 });
        if (active) {
          setSuggestions(res?.data?.content || res?.content || []);
        }
      } catch (err) {
        console.error('Fetch properties error:', err);
      } finally {
        if (active) setSuggestionsLoading(false);
      }
    };

    const delayDebounceFn = setTimeout(fetchProps, 300);

    return () => {
      active = false;
      clearTimeout(delayDebounceFn);
    };
  }, [searchInput]);

  // Click outside suggestions dropdown handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectProperty = (property) => {
    setSelectedProperty(property);
    setSearchInput(property.name || '');
    setPropertyId(property.id);
    setShowSuggestions(false);
    loadPropertyData(property.id);
  };

  const handleSearch = useCallback(async (e) => {
    e?.preventDefault();
    const inputVal = searchInput.trim();
    if (!inputVal) return;

    // Check if it's a UUID
    const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    let idToLoad = '';

    if (uuidRegex.test(inputVal)) {
      idToLoad = inputVal;
      setSelectedProperty(null);
    } else if (selectedProperty && selectedProperty.name === inputVal) {
      idToLoad = selectedProperty.id;
    } else {
      // Find matching property name
      const match = suggestions.find(s => s.name?.toLowerCase() === inputVal.toLowerCase()) || suggestions[0];
      if (match) {
        idToLoad = match.id;
        setSelectedProperty(match);
        setSearchInput(match.name || '');
      } else {
        setLoading(true);
        setError(null);
        try {
          const res = await getAdminProperties({ search: inputVal, size: 1 });
          const content = res?.data?.content || res?.content || [];
          if (content.length > 0) {
            const firstMatch = content[0];
            idToLoad = firstMatch.id;
            setSelectedProperty(firstMatch);
            setSearchInput(firstMatch.name || '');
          } else {
            throw new Error(`Could not find a property matching "${inputVal}". Please select one from the list.`);
          }
        } catch (err) {
          setError(err?.message || 'Property not found');
          setLoading(false);
          return;
        }
      }
    }

    if (idToLoad) {
      setPropertyId(idToLoad);
      setShowSuggestions(false);
      loadPropertyData(idToLoad);
    }
  }, [searchInput, selectedProperty, suggestions, loadPropertyData]);

  const rentSummary = useMemo(() => {
    if (!rentStatus) return null;
    
    let expected = rentStatus.expected ?? rentStatus.totalExpected;
    let collected = rentStatus.collected ?? rentStatus.totalCollected;
    let outstanding = rentStatus.outstanding ?? rentStatus.totalOutstanding;

    if (expected === undefined || collected === undefined || outstanding === undefined) {
      expected = 0;
      collected = 0;
      outstanding = 0;
      const items = rentStatus.items || [];
      items.forEach((item) => {
        expected += Number(item.totalDue || 0);
        collected += Number(item.totalPaid || 0);
        outstanding += Number(item.billRemainingDue || 0);
      });
    }

    return {
      collected,
      expected,
      outstanding,
    };
  }, [rentStatus]);

  return (
    <div>
      <SectionHeader
        title="Payments & Billing"
        subtitle="Look up bills and rent status for any property. Enter a property ID to inspect payment activity."
      />

      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <div ref={suggestionsRef} style={{ position: 'relative', flex: 1, minWidth: 280 }}>
          <FaSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-text-subtle)', fontSize: 13, zIndex: 5 }} />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            placeholder="Search property by name, location, or enter UUID..."
            style={{ width: '100%', padding: '11px 12px 11px 34px', borderRadius: 8, border: '1px solid var(--admin-border-strong)', fontFamily: 'var(--admin-font)', fontSize: 13.5, color: 'var(--admin-text)', background: 'var(--admin-surface)' }}
          />
          {showSuggestions && (
            <div className="payments-suggestions-dropdown">
              {suggestionsLoading && <div className="suggestion-item suggestion-loading">Searching properties...</div>}
              {!suggestionsLoading && suggestions.length === 0 && (
                <div className="suggestion-item no-results">No properties found. Try typing another name.</div>
              )}
              {!suggestionsLoading && suggestions.map((prop) => (
                <div
                  key={prop.id}
                  className="suggestion-item"
                  onClick={() => handleSelectProperty(prop)}
                >
                  <div className="suggestion-prop-name">{prop.name || 'Unnamed Property'}</div>
                  <div className="suggestion-prop-details">
                    <span>📍 {[prop.location?.area, prop.location?.city].filter(Boolean).join(', ') || 'No location'}</span>
                    {prop.contacts?.email && <span className="suggestion-prop-owner"> • 👤 {prop.contacts.email}</span>}
                    {prop.unitCount !== undefined && <span className="suggestion-prop-units"> • {prop.unitCount} units</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <button type="submit" className="admin-btn admin-btn-primary" disabled={loading || !searchInput.trim()}>
          <FaMoneyBillWave style={{ fontSize: 12 }} /> {loading ? 'Loading…' : 'Look up'}
        </button>
      </form>

      {error && <Alert type="error">{error}</Alert>}

      {loading && (
        <div className="admin-grid admin-grid-2">
          <div className="admin-card"><LoadingSkeleton lines={4} /></div>
          <div className="admin-card"><LoadingSkeleton lines={4} /></div>
        </div>
      )}

      {!loading && propertyId && !error && (
        <>
          {/* Rent status summary */}
          {rentSummary && (
            <div className="admin-grid admin-grid-3" style={{ marginBottom: 24 }}>
              <div className="admin-card" style={{ textAlign: 'center' }}>
                <FaFileInvoice style={{ fontSize: 22, color: 'var(--admin-primary)', marginBottom: 8 }} />
                <div style={{ fontSize: 13, color: 'var(--admin-text-muted)', marginBottom: 4 }}>Expected (this month)</div>
                <div style={{ fontFamily: 'var(--admin-font-display)', fontSize: 22, fontWeight: 700 }}>{formatCurrency(rentSummary.expected)}</div>
              </div>
              <div className="admin-card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 22, marginBottom: 8 }}>✅</div>
                <div style={{ fontSize: 13, color: 'var(--admin-text-muted)', marginBottom: 4 }}>Collected</div>
                <div style={{ fontFamily: 'var(--admin-font-display)', fontSize: 22, fontWeight: 700, color: 'var(--admin-success)' }}>{formatCurrency(rentSummary.collected)}</div>
              </div>
              <div className="admin-card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 22, marginBottom: 8 }}>⏳</div>
                <div style={{ fontSize: 13, color: 'var(--admin-text-muted)', marginBottom: 4 }}>Outstanding</div>
                <div style={{ fontFamily: 'var(--admin-font-display)', fontSize: 22, fontWeight: 700, color: 'var(--admin-warning)' }}>{formatCurrency(rentSummary.outstanding)}</div>
              </div>
            </div>
          )}

          {/* Bills table */}
          <div className="admin-card" style={{ padding: 0 }}>
            <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--admin-border)' }}>
              <h3 className="admin-card-title" style={{ margin: 0 }}>
                Bills for {selectedProperty ? selectedProperty.name : 'Property'}
              </h3>
              <p className="admin-card-subtitle" style={{ margin: '4px 0 0 0' }}>
                {selectedProperty 
                  ? `${[selectedProperty.location?.area, selectedProperty.location?.city].filter(Boolean).join(', ') || ''} (ID: ${propertyId})`
                  : propertyId}
              </p>
            </div>
            {bills && bills.length > 0 ? (
              <div className="admin-table-wrap" style={{ border: 'none', borderRadius: 0 }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Unit / Tenant</th>
                      <th>Period</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Due Date</th>
                      <th>Paid Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bills.map((bill, i) => (
                      <tr key={bill.id || i}>
                        <td style={{ fontWeight: 500 }}>{bill.unitLabel || bill.unitName || bill.tenantName || '—'}</td>
                        <td style={{ color: 'var(--admin-text-muted)' }}>{bill.period || bill.month || '—'}</td>
                        <td style={{ fontWeight: 600 }}>{formatCurrency(bill.amount || bill.totalAmount)}</td>
                        <td>{billStatusBadge(bill.status || bill.paymentStatus)}</td>
                        <td style={{ color: 'var(--admin-text-muted)' }}>{formatDate(bill.dueDate)}</td>
                        <td style={{ color: 'var(--admin-text-muted)' }}>{formatDate(bill.paidDate || bill.paymentDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState icon="🧾" title="No bills found" text="This property has no billing records, or the property ID may be invalid." />
            )}
          </div>
        </>
      )}

      {!loading && !propertyId && (
        <div className="admin-card">
          <EmptyState
            icon="💰"
            title="Look up property billing"
            text="Enter a property ID above to view bills and rent-collection status for that property. You can copy a property ID from the Properties page."
          />
        </div>
      )}
    </div>
  );
};

export default Payments;
